import * as mutationsTypes from "../types/mutations";
import { collections } from "@/configs/firebase";
import { firestoreAction } from "vuexfire";
import taskRouter from "@/lib/taskRouter";
import twilioClient, { Twilio } from "@/lib/twilioClient";
import { getUserId, spawnNotification } from "@/helpers";
import { Howl } from "howler";
import Vue from "vue";
import logger from "@/configs/logger";
import { ActionContext } from "@/types";
import router from "../../router";
import { Routes } from "@/router/routes";
import featureConfigs from "@/helpers/featureConfigs";

const incomingRingtone = new Howl({
  src: [
    "https://talkdeskapp.s3.amazonaws.com/production/audio_messages/us_ringingtone.mp3"
  ],
  loop: true,
  autoplay: false
});

const unbindTaskAttributes = firestoreAction(({ unbindFirestoreRef }) => {
  return unbindFirestoreRef("taskAttributes");
});

const unbindAgents = firestoreAction(({ unbindFirestoreRef }) => {
  return unbindFirestoreRef("agents");
});

const unbindLiveCalls = firestoreAction(({ unbindFirestoreRef }) => {
  return unbindFirestoreRef("livecalls");
});

const unbindUpcomingCallbacks = firestoreAction(({ unbindFirestoreRef }) => {
  return unbindFirestoreRef("upcomingCallbacks");
});

const bindUpcomingCallbacks = firestoreAction(
  ({ bindFirestoreRef, state }: any) => {
    const contactUri = state.user.contact_uri || "";
    const uid = contactUri.split(":")[1];
    const userKey = `user/${uid}`;

    return bindFirestoreRef(
      "upcomingCallbacks",
      collections.upcomingCallbacks
        .where("user_key", "==", userKey)
        .where("callback_status", "==", "pending")
        .orderBy("callback_time", "asc")
    );
  }
);

const bindTaskAttributes = firestoreAction(
  ({ bindFirestoreRef }: any, taskId: string) => {
    return bindFirestoreRef(
      "taskAttributes",
      collections.taskAttribute.doc(taskId)
    );
  }
);

const bindAgents = firestoreAction(({ bindFirestoreRef }) => {
  return bindFirestoreRef("agents", collections.user);
});

const bindLiveCalls = firestoreAction(({ bindFirestoreRef }) => {
  return bindFirestoreRef(
    "liveCalls",
    collections.taskAttribute
      .where("call_ended", "==", false)
      .where("reservation_status", "==", "accepted")
  );
});

function cleanUpCall(context: ActionContext) {
  context.dispatch("changeCallMode", false);
  context.dispatch("unbindActiveLead");
  context.dispatch("unbindTaskAttributes");
  context.commit(mutationsTypes.SET_ACTIVE_LEAD, {});
  context.commit(mutationsTypes.SET_CURRENT_TASK, {});
  context.commit(mutationsTypes.SET_TASK_ATTRIBUTES, {});
}

function cleanUpReservation(context: ActionContext) {
  incomingRingtone.stop(); // rings on reservation.created;
  if (context.state.ringing) {
    context.commit(mutationsTypes.SET_RINGING, false);
  }
  context.commit(mutationsTypes.SET_CURRENT_TASK, {});
  context.commit(mutationsTypes.SET_TASK_ATTRIBUTES, {});
  context.dispatch("unbindActiveLead");
}

function changeCallMode(context: ActionContext, status: boolean) {
  context.commit(mutationsTypes.SET_IN_CALL_STATUS, status);
}

function changeInAdminCall(context: ActionContext, status: boolean) {
  context.commit(mutationsTypes.SET_IN_ADMIN_CALL_STATUS, status);
}

function disconnectDevice() {
  logger("log", "disconnecting device");
  twilioClient.device.disconnectAll();
}

async function checkDisconnectedTasks({ state, dispatch }: ActionContext) {
  const userKey = `user/${getUserId(state.user)}`;
  const snapshot = await collections.taskAttribute
    .where("reservation_user_key", "==", userKey)
    .where("call_ended", "==", false)
    .where("agent_disconnected", "==", true)
    .get();

  const disconnectedTasks = snapshot.docs.map(doc => ({
    ...doc.data(),
    id: doc.id
  }));
  if (disconnectedTasks.length > 0) {
    const taskId = disconnectedTasks[0].id;
    logger("log", "agent_disconnected", taskId);

    dispatch("bindTaskAttributes", taskId);
    dispatch("changeCallMode", true);
  }
}

async function getWrapupTasks({ state }: ActionContext) {
  const userKey = `user/${getUserId(state.user)}`;
  const snapshot = await collections.taskAttribute
    .where("reservation_user_key", "==", userKey)
    .where("call_ended", "==", true)
    .where("reservation_status", "==", "wrapup")
    .get();

  return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
}

async function getCallHistory(
  _context: ActionContext,
  uid: string,
  limit = 80
) {
  try {
    const userKey = `user/${uid}`;
    const snap = await collections.taskAttribute
      .where("reservation_user_key", "==", userKey)
      .orderBy("created_ts", "desc")
      .limit(limit)
      .get();

    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (err) {
    Vue.toasted.error("Fetch call history error").goAway(2000);
    logger("error", "Fetch history error", err);
  }
}

export async function hangUp({ state }: ActionContext) {
  try {
    logger("log", "hanging up device");
    twilioClient.device.disconnectAll();
    if (state.taskAttributes.id) {
      await collections.taskAttribute
        .doc(state.taskAttributes.id)
        .update({ call_ended: true });
    }
  } catch (err) {
    logger("error", "Failed to end call, please try again!");
    logger("error", err);
  }
}

async function holdCall({ state }: ActionContext, status: boolean) {
  try {
    await collections.taskAttribute
      .doc(state.taskAttributes.id)
      .update({ lead_hold: status });
  } catch (err) {
    logger("error", "call hold error", err);
  }
}

async function requestTakeOver({ state }: ActionContext, status: boolean) {
  try {
    await collections.taskAttribute
      .doc(state.taskAttributes.id)
      .update({ request_take_over: status });
  } catch (err) {
    logger("error", "request take over error", err);
  }
}

function initTwilio({ state, commit }: ActionContext, token: string) {
  twilioClient.setup(token);

  const handleIncomingConnection = (connection: any) => {
    logger("log", "digit", connection.sendDigits);
    // @ts-ignore
    window._connection = connection;
    connection.accept();
    connection.on("warning", (warningName: string) => {
      logger("warn", "connection warning - ", warningName);
      if (warningName === "low-mos") {
        logger("log", "poor call quality");
        // Vue.toasted.info(
        //   "We have detected poor call quality conditions. You may experience degraded call quality."
        // );
      }
    });
    connection.on("warning-cleared", (warningName: string) => {
      logger("info", "warning cleared - ", warningName);
      // Vue.toasted.info("Call quality is now back to normal").goAway(2000);
    });
    connection.on("mute", (mute: boolean) => {
      logger("log", "connection on muted - ", mute);
    });
    connection.on("reject", () => {
      logger("log", "connection rejected and and transitioned to closed");
    });
    connection.on("reconnecting", () => {
      logger("log", "connectioned failed, automatic reconnection started");
    });
    connection.on("disconnect", () => {
      // @ts-ignore
      window._connection = null;
      logger("log", "call ended");
    });
  };

  twilioClient.device.on("connect", function(_conn: any) {
    commit(mutationsTypes.SET_CALL_CONNECTED, true);
    Vue.toasted.success("Call Connected!").goAway(1000);
    logger("log", "Successfully established call!");
  });

  twilioClient.device.on("disconnect", async function(conn: any) {
    commit(mutationsTypes.SET_CALL_CONNECTED, false);
    logger("warn", "device disconnected");
    logger("log", conn.parameters);
    Vue.toasted.info("Call Ended").goAway(1000);
  });

  twilioClient.device.on("cancel", function(_conn: any) {
    logger("log", "Twilio.Device cancel");
    Vue.toasted.error("Incoming call canceled").goAway(2000);
  });

  twilioClient.device.on("error", function(error: Error) {
    // Vue.toasted.error("Network error");
    logger("log", "Twilio.Device Error: " + error.message);
  });

  twilioClient.device.incoming((conn: any) => {
    logger("log", "Incoming call to device");
    Vue.toasted.success("Call Connecting...").goAway(1000);
    twilioClient.device.audio.incoming(false);
    incomingRingtone.stop();
    handleIncomingConnection(conn);
  });

  twilioClient.device.on("ready", function(_device: any) {
    logger("log", "Twilio.Device Ready!");
    Vue.toasted.success("Your device is Ready!").goAway(1000);
  });

  twilioClient.device.on("offline", function(_device: any) {
    logger("log", "Twilio.Device offline");
  });
}

export function initTaskRouter(
  { commit, dispatch, state }: ActionContext,
  token: string
) {
  taskRouter.initialize(token);

  taskRouter.worker.on("token.expired", function() {
    logger("error", "token expired");
  });

  taskRouter.worker.on("task.timeout", function() {
    logger("log", "task.timeout");
    dispatch("cleanUpReservation");
  });

  taskRouter.worker.on("task.canceled", function() {
    logger("log", "task.canceled");
    dispatch("cleanUpReservation");
  });

  taskRouter.worker.on("reservation.pending", function() {
    logger("log", "reservation.pending");
  });

  taskRouter.worker.on("reservation.timeout", function(reservation: any) {
    logger("log", "reservation.timeout", reservation);
    dispatch("cleanUpReservation");
    const config: any = {
      icon: "mdi-phone",
      iconPack: "mdi",
      action: [{ text: "Close", onClick: Vue.toasted.clear }]
    };
    if (!state.inCallMode && !featureConfigs.getAutoAccept(false)) {
      Vue.toasted.show(
        "You missed a call due to timeout. You only have 7 seconds to pickup.",
        config
      );
    }
  });

  taskRouter.worker.on("reservation.canceled", function(reservation: any) {
    logger("log", "reservation.canceled");
    logger("log", reservation);
    dispatch("cleanUpReservation");
    // @ts-ignore
    const config: any = {
      icon: "mdi-phone",
      iconPack: "mdi",
      action: [{ text: "Close", onClick: Vue.toasted.clear }]
    };
    if (!state.inCallMode) {
      Vue.toasted.show("A call came in but was hung up.", config);
    }
  });

  taskRouter.worker.on("reservation.rescinded", function() {
    logger("log", "reservation.rescinded");
    dispatch("cleanUpReservation");
  });

  taskRouter.worker.on("reservation.accepted", function(_reservation: any) {
    logger("log", "reservation.accepted");
    dispatch("changeCallMode", true);
    incomingRingtone.stop();
    router.replace({ name: Routes.DashBoard });
  });

  taskRouter.worker.on("reservation.created", function(reservation: any) {
    const autoAccept = featureConfigs.getAutoAccept(false);
    logger("log", "reservation.created");
    logger("log", "reservation", reservation);
    logger("log", "auto accept:", autoAccept);
    commit(mutationsTypes.SET_CURRENT_TASK, reservation);
    if (state.inCallMode) {
      logger("warn", "received reservation while in call.");
    } else {
      dispatch(
        "bindTaskAttributes",
        reservation.task.attributes.task_attribute_key.split("/")[1]
      );
      if (autoAccept) {
        dispatch("acceptReservation", { accept: true, reservation });
      } else {
        commit(mutationsTypes.SET_RINGING, true);
        spawnNotification("You have an incoming call");
        incomingRingtone.play();
      }
    }
  });
}

async function muteAgent({ state }: ActionContext, status: boolean) {
  try {
    await collections.taskAttribute
      .doc(state.taskAttributes.id)
      .update({ agent_muted: status });
  } catch (err) {
    logger("error", "agent mute error", err);
  }
}

async function addCallback(
  _context: ActionContext,
  payload: { time: string; leadName: string; leadKey: string; userKey: string }
) {
  const { time, leadName, leadKey, userKey } = payload;
  const date = new Date(time);
  const data = {
    callback_status: "pending",
    callback_time: date,
    callback_source: "disposition_callback",
    lead_name: leadName,
    lead_key: leadKey,
    user_key: userKey
  };
  await collections.upcomingCallbacks.add(data);
  logger("log", "upcoming callback added successfully");
}

async function setDisposition(
  { state, dispatch }: ActionContext,
  payload: {
    disposition: string;
    disposition_note: string;
    taskId: string;
    isCallback: boolean;
  }
) {
  try {
    const { disposition, disposition_note, taskId } = payload;
    const docId = taskId || state.taskAttributes.id;
    let updateTaskAttribute: any = { disposition };
    logger("log", "disposition id here", docId);
    if (disposition_note) {
      updateTaskAttribute["disposition_note"] = disposition_note;
    }

    await collections.taskAttribute.doc(docId).update(updateTaskAttribute);

    logger("log", "disposition set successfully");

    if (state.taskAttributes.call_ended && !payload.isCallback) {
      dispatch("cleanUpCall");
    }
  } catch (err) {
    logger("error", "Set disposition update attribute error ", err);
  }
}

async function updateScriptPrompt(
  { state }: ActionContext,
  scriptPromptItem: any
): Promise<void> {
  await collections.taskAttribute
    .doc(state.taskAttributes.id)
    .collection("script_prompt")
    .doc(scriptPromptItem.id)
    .update(scriptPromptItem);
}

function acceptReservation(
  { commit, dispatch, state }: ActionContext,
  payload: { accept: boolean; reservation: any }
) {
  incomingRingtone.stop();

  const reservation = payload.reservation || state.currentTask;
  commit(mutationsTypes.SET_RINGING, false);

  if (!payload.accept) {
    reservation.reject && reservation.reject();
    dispatch("hangUp");
  } else {
    if (!Object.keys(reservation).length) {
      // When reservation rescinded, timeout, canceled we cleanup the current reservation
      logger("warn", "current task not available to accept call");
    } else {
      logger(
        "log",
        "accepting ping task key" +
          reservation.task.attributes.task_attribute_key
      );
      logger(
        "log",
        "task creation source:",
        reservation.task.attributes.task_creation_source
      );
      if (reservation.task.attributes.task_creation_source === "transfer") {
        logger("log", "taskrouter reservation.call");
        reservation.call(
          "+19165072229",
          `${process.env.VUE_APP_API_ENDPOINT}/render_transfer_twiml?task_attribute_key=${reservation.task.attributes.task_attribute_key}`,
          null,
          "true"
        );
      } else {
        const conferenceOption = {
          ConferenceStatusCallback: `${process.env.VUE_APP_API_ENDPOINT}/conference_callback`,
          ConferenceStatusCallbackEvent: "join,leave",
          ConferenceRecordingStatusCallback: `${process.env.VUE_APP_API_ENDPOINT}/conference_recording_callback`,
          ConferenceRecord: "record-from-start",
          Record: true
          // ConferenceStatusCallbackMethod: 'GET'
          // EndConferenceOnExit: "true"
        };
        const resCb = (err: Error, _res: any) => {
          if (err) {
            logger("error", "reservation.conference error\n", err);
          } else {
            logger("log", "conference initiated");
          }
        };

        // takes a conference call out of the queue and connects it to the WebRTC client
        logger("log", "placing call into conference");
        reservation.conference(
          "+19165072229",
          null,
          null,
          null,
          resCb,
          conferenceOption
        );
      }
    }
  }
}

export default {
  unbindAgents,
  unbindLiveCalls,
  unbindTaskAttributes,
  unbindUpcomingCallbacks,
  updateScriptPrompt,
  addCallback,
  setDisposition,
  muteAgent,
  initTaskRouter,
  initTwilio,
  requestTakeOver,
  holdCall,
  hangUp,
  getCallHistory,
  getWrapupTasks,
  disconnectDevice,
  cleanUpReservation,
  cleanUpCall,
  bindTaskAttributes,
  acceptReservation,
  bindLiveCalls,
  bindAgents,
  changeCallMode,
  changeInAdminCall,
  bindUpcomingCallbacks,
  checkDisconnectedTasks
};
